<script setup lang="ts">
import RegisterDialog from "@/components/dialogs/RegisterDialog.vue";
import SubscriptionRequiredDialog from "@/components/dialogs/SubscriptionRequiredDialog.vue";
import MoveToDirectoryDialog from "@/components/documents/navigator/partials/MoveToDirectoryDialog.vue";
import SaveAsDialog from "@/components/documents/navigator/partials/SaveAsDialog.vue";
import ShareDocumentViaEmailDialog from "@/components/documents/navigator/partials/ShareDocumentViaEmailDialog.vue";

import useDocusign from "@/plugins/docusign";
import { Document } from "@/types/editor/Document";
import axios from "axios";
import printJS from "print-js";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { useStore } from "vuex";

import { useIsMobile } from "@/utils/checkMobile";
import useGTM from "@/utils/gtm";

const router = useRouter();
const { uploadDocumentToDocusign } = useDocusign();

interface MenuItem {
  label: string;
  action?: () => void;
  requiresAuth?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  locked?: boolean;
  hint?: string;
}

const emit = defineEmits([
  "showHistoryModal",
  "showShareHistoryDialog",
  "updateDocument",
  "deletedDocument",
  "toggleAiAssistant",
]);
const store = useStore();

const { document, hasWizardCompleted, buttons } = defineProps<{
  document: Document;
  hasWizardCompleted: boolean;
  buttons: string[];
}>();

const directories = ref([]);
const isTemporaryUser = computed(() => store.state.auth.user.isTemporary);

const $t = useI18n().t;
const isMobile = useIsMobile();

const showShareViaEmailDialog = () => {
  isShareViaEmailDialogOpen.value = true;

  useGTM.pushEvent({
    event: "document_shared",
    document_template: document.template_id,
    share_method: "share by email",
  });
};

const isShareViaEmailDialogOpen = ref(false);
const isSaveAsDialogOpen = ref(false);
const isMoveToDirectoryDialogOpen = ref(false);
const isRegisterDialogOpen = ref(false);
const isSubscriptionDialogOpen = ref(false);

const showHistoryModal = () => {
  emit("showHistoryModal");
};

const showShareHistoryDialog = () => {
  emit("showShareHistoryDialog");
};

const toast = useToast();

const isPaid = computed(() => document.is_paid);

const requiresPayment = computed(() => {
  return !document.is_paid;
});

const copyLink = () => {
  navigator.clipboard.writeText(document.negotiation_url);

  toast.success($t("notifications.link_copied"));

  useGTM.pushEvent({
    event: "document_shared",
    document_template: document.template_id,
    share_method: "copy link",
  });
};

const isReadyForShare = computed(() => document.download_url);
const userHasSubscription = computed(() => store.state.auth.user.hasValidSubscription);

const canSave = computed(() => {
  // Guest users cannot save - need to register
  if (isTemporaryUser.value) return false;

  // For FREE templates - can save anytime except when draft
  if (!requiresPayment.value) {
    return document.status !== "Draft";
  }

  // For PAID templates with subscription - can save anytime except when draft
  if (requiresPayment.value && userHasSubscription.value) {
    return document.status !== "Draft";
  }

  // For PAID templates without subscription - must complete wizard first (P2)
  if (requiresPayment.value && !userHasSubscription.value) {
    return hasWizardCompleted;
  }

  return false;
});

const canSaveAs = computed(() => {
  // Guest users cannot save as - need to register
  if (isTemporaryUser.value) return false;

  // For FREE templates - can save as anytime
  if (!requiresPayment.value) return true;

  // For PAID templates with subscription - can save as anytime
  if (requiresPayment.value && userHasSubscription.value) return true;

  // For PAID templates without subscription - must complete wizard first (P2)
  if (requiresPayment.value && !userHasSubscription.value) {
    return hasWizardCompleted;
  }

  return false;
});

const hasDirectories = computed(() => directories.value && directories.value.length > 0);

const canMoveToFolder = computed(() => {
  // Need subscription first
  if (isTemporaryUser.value || !userHasSubscription.value) return false;

  // Need to have at least one folder created
  return hasDirectories.value;
});

const isInSecondPart = computed(() => {
  // You're in the second part when you've completed the wizard steps (questions)
  // and moved to the variables input section
  const hasCompletedWizardSteps = store.state.editor.wizard.steps?.every(step => 
    step.subSteps?.every(subStep => 
      subStep.questions?.every(question => question.answer !== null)
    )
  ) || false;
  
  // Or if you're actively filling variables (variables wizard has started)
  const isFillingVariables = store.state.editor.are_variables_set || 
                            Object.keys(store.state.editor.variables).length > 0;
  
  return hasCompletedWizardSteps || isFillingVariables;
});

const printDocument = async () => {
  if (isTemporaryUser.value) {
    showRegisterDialog();
    return;
  }

  if (!isPaid.value && !userHasSubscription.value) {
    showSubscriptionDialog();
    return;
  }

  try {
    // Extract path from download_url (backend may return full URL with wrong host)
    let downloadPath = document.download_url;

    // If it's a full URL, extract just the path + query string
    if (downloadPath.startsWith('http://') || downloadPath.startsWith('https://')) {
      try {
        const urlObj = new URL(downloadPath);
        downloadPath = urlObj.pathname + urlObj.search;
      } catch (e) {
        console.warn('Failed to parse download_url, using as-is:', e);
      }
    }

    // Fetch document as blob with authentication token (added by axios interceptor)
    const response = await axios.get(downloadPath, {
      responseType: "blob",
    });

    // Create blob URL for printing
    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);

    // Print using the blob URL
    printJS(url);

    // Clean up after a delay
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    useGTM.pushEvent({
      event: "document_printed",
      document_template: document.template_id,
    });
  } catch (error) {
    console.error("Error printing document:", error);
    toast.error($t("notifications.print_error"));
  }
};

const printDocumentForVisitor = async () => {
  try {
    // Extract path from download_url (backend may return full URL with wrong host)
    let downloadPath = document.download_url;

    // If it's a full URL, extract just the path + query string
    if (downloadPath.startsWith('http://') || downloadPath.startsWith('https://')) {
      try {
        const urlObj = new URL(downloadPath);
        downloadPath = urlObj.pathname + urlObj.search;
      } catch (e) {
        console.warn('Failed to parse download_url, using as-is:', e);
      }
    }

    // Fetch document as blob with authentication token (added by axios interceptor)
    const response = await axios.get(downloadPath, {
      responseType: "blob",
    });

    // Create blob URL for printing
    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);

    // Print using the blob URL
    printJS(url);

    // Clean up after a delay
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    useGTM.pushEvent({
      event: "document_printed",
      document_template: document.template_id,
    });
  } catch (error) {
    console.error("Error printing document:", error);
    toast.error($t("notifications.print_error"));
  }
};

const downloadDocument = async () => {
  try {
    // Extract path from download_url (backend may return full URL with wrong host)
    let downloadPath = document.download_url;

    // If it's a full URL, extract just the path + query string
    if (downloadPath.startsWith('http://') || downloadPath.startsWith('https://')) {
      try {
        const urlObj = new URL(downloadPath);
        downloadPath = urlObj.pathname + urlObj.search;
      } catch (e) {
        console.warn('Failed to parse download_url, using as-is:', e);
      }
    }

    // Fetch document as blob with authentication token (added by axios interceptor)
    const response = await axios.get(downloadPath, {
      responseType: "blob",
    });

    // Create file from blob
    const file = new File([response.data], `${document.name || "document"}.pdf`, {
      type: "application/pdf",
    });

    // Create download link and trigger download
    const url = URL.createObjectURL(file);
    const link = window.document.createElement("a");
    link.href = url;
    link.setAttribute("download", file.name);
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    URL.revokeObjectURL(url);

    useGTM.pushEvent({
      event: "document_downloaded",
      document_template: document.template_id,
    });
  } catch (error) {
    console.error("Error downloading document:", error);
    toast.error($t("notifications.download_error"));
  }
};

const showRegisterDialog = () => {
  isRegisterDialogOpen.value = true;
};

const showSubscriptionDialog = () => {
  isSubscriptionDialogOpen.value = true;
};

const edit = () => {
  if (document.template_id) {
    router.push({
      path: '/document-questions',
      query: {
        template: document.template_id,
        documentId: document.id
      }
    });
  } else {
    // Fallback to old route if no template_id
    router.push(`/new-document-creator/${document.id}`);
  }
};

const showComments = () => {
  try {
    // Extract token from negotiation_url for ALL users
    // The /approve endpoint requires token-based auth, not Bearer token
    if (!document.negotiation_url) {
      toast.error($t("notifications.default_error"));
      return;
    }

    const commentsPageUrl = new URL(document.negotiation_url);
    const params = new URLSearchParams(commentsPageUrl.search);
    const token = params.get("token");

    if (!token) {
      toast.error($t("notifications.default_error"));
      return;
    }

    router.push(`/documents/${document.id}/approve?token=${token}`);
  } catch (error) {
    console.error("Error parsing negotiation URL:", error);
    toast.error($t("notifications.default_error"));
  }
};

const signDocument = async () => {
  uploadDocumentToDocusign(document.id);
};

const save = () => {
  if (isTemporaryUser.value) {
    showRegisterDialog();
    return;
  }

  if (!canSave.value) {
    if (requiresPayment.value && !userHasSubscription.value && !hasWizardCompleted) {
      toast.info($t("documents.save_disabled_payment_required"));
    } else {
      toast.info($t("documents.save_disabled_phase1"));
    }
    return;
  }

  if (!store.state.editor.someChangesToSave) {
    toast.info($t("notifications.no_changes_to_save"));
    return;
  }

  axios
    .post(`api/documents/${document.id}?_method=PUT`, {
      name: document.name,
      content: store.state.editor.content,
      template_id: store.state.editor.templateId,
      directory_id: document.directory?.id,
    })
    .then((response) => {
      toast.success($t("notifications.document_saved"));
      store.commit("editor/switchContentChangeStatus", false);
    })
    .catch((error) => {
      if (error.response.status === 402) {
        toast.error($t("documents.subscription_or_single_payment_required"));
      } else {
        toast.error($t("notifications.document_save_error"));
      }
    });
};

const saveAs = () => {
  if (isTemporaryUser.value) {
    showRegisterDialog();
    return;
  }

  isSaveAsDialogOpen.value = true;
};

const moveToDirectory = () => {
  if (isTemporaryUser.value) {
    showRegisterDialog();
    return;
  }

  if (!userHasSubscription.value) {
    showSubscriptionDialog();
    return;
  }

  isMoveToDirectoryDialogOpen.value = true;
};

const deleteDocument = () => {
  axios
    .delete(`api/documents/${document.id}`)
    .then(() => {
      toast.success($t("notifications.document_deleted"));
      emit("deletedDocument");
    })
    .catch(() => {
      toast.error($t("notifications.document_delete_error"));
    });
};

const duplicate = () => {
  if (isTemporaryUser.value) {
    showRegisterDialog();
    return;
  }

  if (!userHasSubscription.value) {
    showSubscriptionDialog();
    return;
  }

  axios
    .post(`api/documents/${document.id}/copy`)
    .then((response) => {
      toast.success($t("notifications.document_duplicated"));
      if (document.template_id) {
        router.push({
          path: '/document-questions',
          query: {
            template: document.template_id,
            documentId: response.data.data.id
          }
        });
      } else {
        // Fallback to old route if no template_id
        router.push({
          path: `/new-document-creator/${response.data.data.id}`,
        });
      }
    })
    .catch(() => {
      toast.error($t("notifications.document_duplicate_error"));
    });
};

const toggleAiAssistant = () => {
  emit("toggleAiAssistant");
};

const visitorItems: MenuItem[] = [
  {
    label: $t("common.download"),
    action: downloadDocument,
    requiresAuth: false,
    disabled: document.status === "Draft" || hasWizardCompleted === false,
    locked: hasWizardCompleted === false,
    hint: hasWizardCompleted === false ? $t("documents.download_locked_hint") : undefined,
  },
  {
    label: $t("common.print"),
    action: printDocumentForVisitor,
    requiresAuth: false,
    disabled: false,
    hidden: isMobile,
  },
  {
    label: $t("documents.show_history"),
    action: showHistoryModal,
  },
];

const editItems: MenuItem[] = [
  {
    label: $t("common.edit"),
    action: edit,
  },
  {
    label: $t("documents.show_comments"),
    action: showComments,
    requiresAuth: true,
    disabled: !document.download_url || hasWizardCompleted === false,
    locked: !document.download_url || hasWizardCompleted === false,
    hint: !document.download_url || hasWizardCompleted === false
      ? $t("documents.comments_disabled_phase1")
      : undefined,
  },
  {
    label: $t("documents.move_to_directory"),
    action: moveToDirectory,
    requiresAuth: true,
    disabled: !canMoveToFolder.value,
    locked: !canMoveToFolder.value,
    hint: !canMoveToFolder.value
      ? isTemporaryUser.value || !userHasSubscription.value
        ? $t("documents.move_to_folder_subscription_required")
        : $t("documents.move_to_folder_no_folders")
      : undefined,
  },
  {
    label: $t("common.print"),
    action: printDocument,
    requiresAuth: false,
    disabled: document.status === "Draft" || !hasWizardCompleted,
    locked: !hasWizardCompleted,
    hint: !hasWizardCompleted ? $t("documents.print_disabled_phase1") : undefined,
    hidden: isMobile,
  },
  {
    label: $t("common.download"),
    action: downloadDocument,
    requiresAuth: false,
    disabled: document.status === "Draft" || hasWizardCompleted === false,
    locked: hasWizardCompleted === false,
    hint: hasWizardCompleted === false ? $t("documents.download_locked_hint") : undefined,
  },
  {
    label: $t("common.sign"),
    action: signDocument,
    requiresAuth: true,
    hint: $t("documents.sign_hint"),
    disabled: hasWizardCompleted === false,
  },
];

const fileItems = computed<MenuItem[]>(() => {
  return [
    {
      label: $t("common.save"),
      action: save,
      disabled: !canSave.value || !store.state.editor.someChangesToSave,
      locked: !canSave.value || !store.state.editor.someChangesToSave,
      hint: !canSave.value
        ? requiresPayment.value && !userHasSubscription.value && !hasWizardCompleted
          ? $t("documents.save_disabled_payment_required")
          : $t("documents.save_disabled_phase1")
        : !store.state.editor.someChangesToSave
          ? $t("documents.no_changes_to_save")
          : undefined,
    },
    {
      label: $t("common.change_name"),
      action: saveAs,
      requiresAuth: true,
      disabled: !canSaveAs.value,
      locked: !canSaveAs.value,
      hint: !canSaveAs.value
        ? isTemporaryUser.value
          ? $t("documents.register_required")
          : requiresPayment.value && !userHasSubscription.value && !hasWizardCompleted
            ? $t("documents.save_disabled_payment_required")
            : $t("documents.save_disabled_phase1")
        : undefined,
    },
    {
      label: $t("documents.show_comments"),
      action: showComments,
      requiresAuth: true,
      disabled: isTemporaryUser.value || !document.download_url || hasWizardCompleted === false,
      locked: isTemporaryUser.value || !document.download_url || hasWizardCompleted === false,
      hint: isTemporaryUser.value
        ? $t("documents.register_required_feature")
        : !document.download_url || hasWizardCompleted === false
          ? $t("documents.comments_disabled_phase1")
          : undefined,
    },
    {
      label: $t("documents.move_to_directory"),
      action: moveToDirectory,
      requiresAuth: true,
      disabled: !canMoveToFolder.value,
      locked: !canMoveToFolder.value,
      hint: !canMoveToFolder.value
        ? isTemporaryUser.value || !userHasSubscription.value
          ? $t("documents.move_to_folder_subscription_required")
          : $t("documents.move_to_folder_no_folders")
        : undefined,
    },
    {
      label: $t("common.download"),
      action: downloadDocument,
      requiresAuth: false,
      disabled: document.status === "Draft" || hasWizardCompleted === false,
      locked: hasWizardCompleted === false,
      hint: hasWizardCompleted === false ? $t("documents.download_locked_hint") : undefined,
    },
    {
      label: $t("documents.duplicate"),
      action: duplicate,
      requiresAuth: true,
      locked: isTemporaryUser.value || !userHasSubscription.value,
      hint:
        isTemporaryUser.value || !userHasSubscription.value
          ? $t("documents.duplicate_locked_hint")
          : undefined,
    },
    {
      label: $t("common.print"),
      action: printDocument,
      requiresAuth: false,
      disabled: (requiresPayment.value && !userHasSubscription.value && !hasWizardCompleted) || document.status === "Draft" || !hasWizardCompleted,
      locked: (requiresPayment.value && !userHasSubscription.value && !hasWizardCompleted) || !hasWizardCompleted,
      hint: !hasWizardCompleted
        ? $t("documents.print_disabled_phase1")
        : (requiresPayment.value && !userHasSubscription.value
            ? $t("documents.sign_locked_hint")
            : undefined),
      hidden: isMobile,
    },
    {
      label: $t("documents.show_history"),
      action: showHistoryModal,
      disabled: !isInSecondPart.value,
      locked: !isInSecondPart.value,
      hint: !isInSecondPart.value ? $t("documents.history_disabled_early") : undefined,
    },
    {
      label: $t("common.sign"),
      action: signDocument,
      requiresAuth: true,
      disabled: isTemporaryUser.value || !hasWizardCompleted || (requiresPayment.value && !hasWizardCompleted),
      locked: isTemporaryUser.value || !hasWizardCompleted || (requiresPayment.value && !hasWizardCompleted),
      hint: isTemporaryUser.value
        ? $t("documents.register_required_feature")
        : !hasWizardCompleted
          ? $t("documents.sign_hint")
          : undefined,
    },
  ];
});

const shareItems: MenuItem[] = [
  {
    label: $t("creator.copy_link"),
    action: copyLink,
    requiresAuth: false,
    disabled: document.status === "Draft",
  },
  {
    label: $t("creator.share_email"),
    action: showShareViaEmailDialog,
    requiresAuth: false,
    disabled: document.status === "Draft",
  },
  {
    label: $t("documents.send_to_verification"),
    action: showShareViaEmailDialog,
    requiresAuth: false,
    disabled: document.status === "Draft",
  },
  {
    label: $t("documents.show_history"),
    action: showShareHistoryDialog,
  },
];

const previewShareItems: MenuItem[] = [
  {
    label: $t("creator.copy_link"),
    action: copyLink,
    requiresAuth: false,
    disabled: document.status === "Draft",
  },
  {
    label: $t("creator.share_email"),
    action: showShareViaEmailDialog,
    requiresAuth: false,
    disabled: document.status === "Draft",
  },
  {
    label: $t("documents.send_to_verification"),
    action: showShareViaEmailDialog,
    requiresAuth: false,
    disabled: document.status === "Draft",
  },
];

const handleDocumentUpdated = () => {
  emit("updateDocument");
};

const handleSaveByShortcut = (event) => {
  const isShortcutClicked = event.ctrlKey && event.key === "s";

  if (isShortcutClicked) {
    event.preventDefault();
    save();
  }
};

const handleItemClick = (item: MenuItem) => {
  if (item.locked || item.disabled) {
    return;
  }
  if (item.action) {
    item.action();
  }
};

onMounted(async () => {
  try {
    if (userHasSubscription.value) {
      const { data } = await axios.get("/api/directories");
      directories.value = data.data;
    }
  } catch (error) {
    directories.value = [];
  }

  window.addEventListener("keydown", handleSaveByShortcut);
});
onUnmounted(() => {
  window.removeEventListener("keydown", handleSaveByShortcut);
});
</script>
<template>
  <div>
    <!-- Desktop View -->
    <div class="navigator" v-if="!isMobile">
      <!-- VISITOR -->
      <v-menu v-if="buttons.includes('visitor')">
        <template v-slot:activator="{ props }">
          <v-btn v-bind="props" variant="text" class="padding file-btn border-sm">
            {{ $t("common.file") }}

            <template v-slot:append>
              <v-icon icon="mdi-menu-down"></v-icon>
            </template>
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="(item, index) in visitorItems.filter((item) => !item.hidden)"
            :key="index"
            :value="index"
            @click="handleItemClick(item)"
            :disabled="item.disabled || item.locked"
          >
            <template v-if="item.locked" v-slot:prepend>
              <v-icon icon="mdi-lock"></v-icon>
            </template>
            <v-list-item-title>{{ item.label }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- EDIT -->
      <v-menu v-if="buttons.includes('edit')">
        <template v-slot:activator="{ props }">
          <v-btn v-bind="props" variant="text" class="padding file-btn border-sm">
            {{ $t("common.file") }}

            <template v-slot:append>
              <v-icon icon="mdi-menu-down"></v-icon>
            </template>
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="(item, index) in editItems.filter((item) => !item.hidden)"
            :key="index"
            :value="index"
            @click="handleItemClick(item)"
            :disabled="item.disabled || item.locked"
          >
            <template v-if="item.locked" v-slot:prepend>
              <v-icon icon="mdi-lock"></v-icon>
            </template>

            <v-tooltip location="bottom" :text="item.hint">
              <template v-slot:activator="{ props }">
                <div
                  v-bind="item.hint && (item.disabled || item.locked) && props"
                  style="pointer-events: auto"
                >
                  <v-list-item-title>{{ item.label }}</v-list-item-title>
                </div>
              </template>
            </v-tooltip>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- FILE -->
      <v-menu v-if="buttons.includes('file')">
        <template v-slot:activator="{ props }">
          <v-btn v-bind="props" variant="text" class="padding file-btn border-sm">
            {{ $t("common.file") }}

            <template v-slot:append>
              <v-icon icon="mdi-menu-down"></v-icon>
            </template>
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="(item, index) in fileItems.filter((item) => !item.hidden)"
            :key="index"
            :value="index"
            @click="handleItemClick(item)"
            :disabled="item.disabled || item.locked"
          >
            <template v-if="item.locked" v-slot:prepend>
              <v-icon icon="mdi-lock"></v-icon>
            </template>

            <v-tooltip location="bottom" :text="item.hint">
              <template v-slot:activator="{ props }">
                <div
                  v-bind="item.hint && (item.disabled || item.locked) && props"
                  style="pointer-events: auto"
                >
                  <v-list-item-title>{{ item.label }}</v-list-item-title>
                </div>
              </template>
            </v-tooltip>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- PREVIEW SHARE -->
      <v-menu v-if="buttons.includes('preview-share')">
        <template v-slot:activator="{ props }">
          <v-btn
            variant="text"
            class="padding share-btn border-sm"
            v-bind="props"
            :disabled="!isReadyForShare"
          >
            {{ $t("common.share") }}

            <template v-slot:append>
              <img src="/src/assets/icons/share.svg" alt="" />
            </template>
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="(item, index) in previewShareItems.filter((item) => !item.hidden)"
            :key="index"
            :value="index"
            @click="handleItemClick(item)"
          >
            <template v-if="item.locked" v-slot:prepend>
              <v-icon icon="mdi-lock"></v-icon>
            </template>
            <v-list-item-title>{{ item.label }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- SHARE -->
      <v-menu v-if="buttons.includes('share')">
        <template v-slot:activator="{ props }">
          <v-btn
            variant="text"
            class="padding share-btn border-sm"
            v-bind="props"
            :disabled="!isReadyForShare"
          >
            {{ $t("common.share") }}

            <template v-slot:append>
              <img src="/src/assets/icons/share.svg" alt="" />
            </template>
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="(item, index) in shareItems.filter((item) => !item.hidden)"
            :key="index"
            :value="index"
            @click="handleItemClick(item)"
          >
            <template v-if="item.locked" v-slot:prepend>
              <v-icon icon="mdi-lock"></v-icon>
            </template>
            <v-list-item-title>{{ item.label }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- DELETE -->
      <v-btn v-if="buttons.includes('delete')" color="primary" @click="deleteDocument">
        {{ $t("common.delete") }}
      </v-btn>

      <!-- AI Assistant -->
      <v-btn
        v-if="buttons.includes('ai')"
        class="assistant-btn"
        color="var(--light-grey)"
        @click="toggleAiAssistant"
        variant="flat"
      >
        <template v-slot:prepend>
          <img src="/src/assets/icons/virtual-assistant-header.svg" alt="" />
        </template>
        {{ $t("chat.ai_assistant") }}
      </v-btn>
    </div>

    <!-- Mobile View -->
    <div v-else>
      <v-menu width="100%" class="navigator-mobile">
        <template v-slot:activator="{ props }">
          <v-btn class="menu-button" variant="flat" v-bind="props">
            <img src="/images/icons/dots-line.svg" alt="menu icon" />
          </v-btn>
        </template>

        <!-- VISITOR -->
        <v-list rounded="0" density="compact" v-if="buttons.includes('visitor')">
          <v-list-subheader>
            {{ $t("common.file") }}
          </v-list-subheader>
          <v-list>
            <v-list-item
              v-for="(item, index) in visitorItems.filter((item) => !item.hidden)"
              :key="index"
              :value="index"
              @click="handleItemClick(item)"
              :disabled="item.disabled || item.locked"
            >
              <template v-if="item.requiresAuth && item.locked" v-slot:prepend>
                <v-icon icon="mdi-lock"></v-icon>
              </template>
              <v-list-item-title>{{ item.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-list>

        <!-- EDIT -->
        <v-list rounded="0" density="compact" v-if="buttons.includes('edit')">
          <v-list-subheader>
            {{ $t("common.file") }}
          </v-list-subheader>
          <v-list>
            <v-list-item
              v-for="(item, index) in editItems.filter((item) => !item.hidden)"
              :key="index"
              :value="index"
              @click="handleItemClick(item)"
              :disabled="item.disabled || item.locked"
            >
              <template v-if="item.requiresAuth && item.locked" v-slot:prepend>
                <v-icon icon="mdi-lock"></v-icon>
              </template>
              <v-list-item-title>{{ item.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-list>

        <!-- FILE -->
        <v-list rounded="0" density="compact" v-if="buttons.includes('file')">
          <v-list-subheader>
            {{ $t("common.file") }}
          </v-list-subheader>
          <v-list-item
            v-for="(item, index) in fileItems.filter((item) => !item.hidden)"
            :key="index"
            :value="index"
            @click="handleItemClick(item)"
            :disabled="item.disabled || item.locked"
          >
            <template v-if="item.locked" v-slot:prepend>
              <v-icon icon="mdi-lock"></v-icon>
            </template>
            <v-list-item-title>{{ item.label }}</v-list-item-title>
          </v-list-item>
        </v-list>

        <!-- PREVIEW SHARE !-->
        <v-list rounded="0" density="compact" v-if="buttons.includes('preview-share')">
          <v-list-subheader>
            {{ $t("common.share") }}
          </v-list-subheader>
          <v-list-item
            v-for="(item, index) in previewShareItems.filter((item) => !item.hidden)"
            :key="index"
            :value="index"
            @click="handleItemClick(item)"
          >
            <template v-if="item.locked || item.requiresAuth" v-slot:prepend>
              <v-icon icon="mdi-lock"></v-icon>
            </template>
            <v-list-item-title>{{ item.label }}</v-list-item-title>
          </v-list-item>
        </v-list>

        <!-- SHARE !-->
        <v-list rounded="0" density="compact" v-if="buttons.includes('share')">
          <v-list-subheader>
            {{ $t("common.share") }}
          </v-list-subheader>
          <v-list-item
            v-for="(item, index) in shareItems.filter((item) => !item.hidden)"
            :disabled="!isReadyForShare"
            :key="index"
            :value="index"
            @click="handleItemClick(item)"
          >
            <template v-if="item.locked" v-slot:prepend>
              <v-icon icon="mdi-lock"></v-icon>
            </template>
            <v-list-item-title>{{ item.label }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <!-- SHARE DOCUMENT VIA EMAIL DIALOG -->
    <ShareDocumentViaEmailDialog
      :is-dialog-open="isShareViaEmailDialogOpen"
      @close-dialog="isShareViaEmailDialogOpen = false"
      :document="document"
    />

    <SaveAsDialog
      :is-dialog-open="isSaveAsDialogOpen"
      @close-dialog="isSaveAsDialogOpen = false"
      @document-updated="handleDocumentUpdated"
      :document="document"
      :key="document.name"
    />

    <MoveToDirectoryDialog
      :is-dialog-open="isMoveToDirectoryDialogOpen"
      @close-dialog="isMoveToDirectoryDialogOpen = false"
      @document-updated="handleDocumentUpdated"
      :document="document"
      :directories="directories"
    />

    <RegisterDialog
      :is-dialog-open="isRegisterDialogOpen"
      @close-dialog="isRegisterDialogOpen = false"
    />

    <SubscriptionRequiredDialog
      :is-dialog-open="isSubscriptionDialogOpen"
      @close-dialog="isSubscriptionDialogOpen = false"
    />
  </div>
</template>

<style scoped lang="scss">
.padding {
  padding: 20px !important;
}

.navigator-mobile {
  width: 100%;
  display: block;
  margin: 0 !important;
  padding: 0 !important;
}

.menu-button {
  min-width: 44px !important;
  max-width: 44px !important;
  min-height: 42px !important;
  max-height: 42px !important;
  border: 1px solid var(--grey) !important;
  background-color: white !important;
}

.navigator {
  display: flex;
  gap: 15px;

  .v-btn {
    text-transform: none;
    display: flex;
    height: 42px;
  }

  .file-btn {
    gap: 100px;
  }

  .share-btn {
    gap: 50px;
  }

  .assistant-btn {
    font-weight: 700;
  }
}
</style>
